import React from "react";
import {
  Box,
  Button,
  Paper,
  Typography,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  CircularProgress,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { Send as SendIcon } from "@mui/icons-material";
import { getTypeColor } from "./utils";

export default function SentMessagesTab({
  sentMessages,
  loading,
  messageFilters,
  setMessageFilters,
  onOpenSendDialog,
}) {
  const theme = useTheme();

  return (
    <>
      <Box sx={{ ml: 3 }}>
        <Button
          variant="contained"
          startIcon={<SendIcon />}
          onClick={onOpenSendDialog}
          sx={{
            textTransform: "none",
            fontWeight: 600,
          }}
        >
          Send New Message
        </Button>
      </Box>

      {/* Message Filters */}
      <Paper
        sx={{
          p: 3,
          mb: 3,
          backgroundColor: theme.palette.background.paper,
        }}
      >
        <Typography variant="h6" gutterBottom fontWeight={600}>
          Filter Messages
        </Typography>
        <Grid container spacing={2}>
          <Grid item xs={12} md={4}>
            <TextField
              fullWidth
              label="Search"
              placeholder="Search in title or message..."
              value={messageFilters.search}
              onChange={(e) =>
                setMessageFilters({ ...messageFilters, search: e.target.value })
              }
              size="small"
            />
          </Grid>
          <Grid item xs={12} md={2}>
            <FormControl fullWidth size="small">
              <InputLabel>Type</InputLabel>
              <Select
                value={messageFilters.type}
                label="Type"
                onChange={(e) =>
                  setMessageFilters({ ...messageFilters, type: e.target.value })
                }
              >
                <MenuItem value="">All</MenuItem>
                <MenuItem value="info">Info</MenuItem>
                <MenuItem value="warning">Warning</MenuItem>
                <MenuItem value="announcement">Announcement</MenuItem>
                <MenuItem value="violation">Violation</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={2}>
            <FormControl fullWidth size="small">
              <InputLabel>Category</InputLabel>
              <Select
                value={messageFilters.category}
                label="Category"
                onChange={(e) =>
                  setMessageFilters({ ...messageFilters, category: e.target.value })
                }
              >
                <MenuItem value="">All</MenuItem>
                <MenuItem value="profile_violation">Profile Violation</MenuItem>
                <MenuItem value="chat_violation">Chat Violation</MenuItem>
                <MenuItem value="content_violation">Content Violation</MenuItem>
                <MenuItem value="general">General</MenuItem>
                <MenuItem value="account">Account</MenuItem>
                <MenuItem value="feature">Feature</MenuItem>
                <MenuItem value="update">Update</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={2}>
            <FormControl fullWidth size="small">
              <InputLabel>Severity</InputLabel>
              <Select
                value={messageFilters.severity}
                label="Severity"
                onChange={(e) =>
                  setMessageFilters({
                    ...messageFilters,
                    severity: e.target.value,
                  })
                }
              >
                <MenuItem value="">All</MenuItem>
                <MenuItem value="low">Low</MenuItem>
                <MenuItem value="medium">Medium</MenuItem>
                <MenuItem value="high">High</MenuItem>
                <MenuItem value="critical">Critical</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={2}>
            <FormControl fullWidth size="small">
              <InputLabel>Status</InputLabel>
              <Select
                value={messageFilters.isRead}
                label="Status"
                onChange={(e) =>
                  setMessageFilters({ ...messageFilters, isRead: e.target.value })
                }
              >
                <MenuItem value="">All</MenuItem>
                <MenuItem value="true">Read</MenuItem>
                <MenuItem value="false">Unread</MenuItem>
              </Select>
            </FormControl>
          </Grid>
        </Grid>
        <Box sx={{ mt: 2 }}>
          <Button
            size="small"
            variant="outlined"
            onClick={() =>
              setMessageFilters({
                type: "",
                category: "",
                severity: "",
                isRead: "",
                search: "",
              })
            }
            sx={{ textTransform: "none" }}
          >
            Clear Filters
          </Button>
        </Box>
      </Paper>

      {/* Sent Messages History */}
      <Typography
        variant="h6"
        gutterBottom
        fontWeight={600}
        sx={{ mb: 2, ml: 3 }}
      >
        Recently Sent Messages ({sentMessages.length})
      </Typography>
      {loading ? (
        <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
          <CircularProgress color="primary" />
        </Box>
      ) : (
        <TableContainer
          component={Paper}
          sx={{ backgroundColor: theme.palette.background.paper, px: 3 }}
        >
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Title</TableCell>
                <TableCell>Type</TableCell>
                <TableCell>Category</TableCell>
                <TableCell>Recipient</TableCell>
                <TableCell>Sent At</TableCell>
                <TableCell>Status</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {sentMessages.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} align="center">
                    No messages sent yet
                  </TableCell>
                </TableRow>
              ) : (
                sentMessages.map((message) => {
                  const isBroadcast = message.isBroadcast || !message.userId;
                  
                  return (
                    <TableRow key={message._id}>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          {message.title}
                          {isBroadcast && (
                            <Chip
                              label="Broadcast"
                              color="secondary"
                              size="small"
                              variant="outlined"
                              sx={{ fontWeight: 600 }}
                            />
                          )}
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={message.type}
                          color={getTypeColor(message.type)}
                          size="small"
                        />
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={message.category || "general"}
                          variant="outlined"
                          size="small"
                        />
                      </TableCell>
                      <TableCell>
                        {isBroadcast ? (
                          <Chip
                            label="All Users"
                            color="info"
                            size="small"
                            variant="filled"
                          />
                        ) : (
                          `${message.userId?.name.firstName || ""} ${
                            message.userId?.name.lastName || ""
                          }`.trim() ||
                          message.userId?.email ||
                          "User"
                        )}
                      </TableCell>
                      <TableCell>
                        {new Date(message.createdAt).toLocaleString()}
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={message.isRead ? "Read" : "Unread"}
                          color={message.isRead ? "success" : "default"}
                          size="small"
                        />
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </>
  );
}
